import { useState, useEffect, useRef } from "react";
import { X, CheckCircle, Loader2 } from "lucide-react";
import confetti from "canvas-confetti";

const phoneRegex = /^\+?[\d\s\-().]{7,20}$/;

const WaitlistModal = ({ open, onClose }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");
  const nameRef = useRef(null);
  const backdropRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName("");
      setEmail("");
      setPhone("");
      setStatus("idle");
      setMessage("");
      setTimeout(() => nameRef.current?.focus(), 100);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const isFormValid = name.trim().length >= 2 && email && phoneRegex.test(phone);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid || status === "loading") return;

    if (!phoneRegex.test(phone)) {
      setStatus("error");
      setMessage("Please enter a valid phone number.");
      return;
    }

    setStatus("loading");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email, phone: phone.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setStatus("error");
        setMessage(data.detail || "Something went wrong. Please try again.");
        return;
      }
      if (data.status === "exists") {
        setStatus("exists");
        setMessage(data.message);
      } else {
        setStatus("success");
        setMessage(data.message);
        confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 }, colors: ["#6C5CE7", "#ffc366", "#34a853", "#4285f4"] });
      }
    } catch {
      setStatus("error");
      setMessage("Network error. Please try again.");
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) onClose();
  };

  if (!open) return null;

  const isComplete = status === "success" || status === "exists";

  const inputClass = "w-full px-4 py-3 text-[14px] text-[#202124] bg-white border border-[#dadce0] rounded-lg outline-none focus:border-[#6C5CE7] focus:ring-2 focus:ring-[#6C5CE7]/20 transition-all placeholder:text-[#80868b]";

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 animate-in fade-in duration-200"
      data-testid="waitlist-modal-backdrop"
    >
      <div
        className="relative w-full max-w-md bg-white rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.16)] overflow-hidden animate-in zoom-in-95 duration-200"
        data-testid="waitlist-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#f1f3f4] text-[#5f6368] transition-colors z-10"
          data-testid="waitlist-modal-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-8 sm:p-10">
          {!isComplete ? (
            <>
              <div className="text-center mb-7">
                <div className="w-12 h-12 rounded-2xl bg-[#fff4eb] flex items-center justify-center mx-auto mb-4">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6C5CE7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 18 22 12 16 6"/>
                    <polyline points="8 6 2 12 8 18"/>
                  </svg>
                </div>
                <h2 className="text-[22px] sm:text-[26px] font-normal text-[#202124] leading-tight" data-testid="waitlist-modal-title">
                  Get early access
                </h2>
                <p className="mt-2 text-[14px] text-[#5f6368] leading-relaxed max-w-xs mx-auto">
                  Join the waitlist for Helocore Studio and Helocore Cloud. Be among the first to build what's next.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3.5" data-testid="waitlist-form">
                <div>
                  <label htmlFor="waitlist-name" className="block text-[13px] font-medium text-[#202124] mb-1.5">
                    Full name
                  </label>
                  <input
                    ref={nameRef}
                    id="waitlist-name"
                    type="text"
                    required
                    minLength={2}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Smith"
                    className={inputClass}
                    data-testid="waitlist-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="waitlist-email" className="block text-[13px] font-medium text-[#202124] mb-1.5">
                    Work email
                  </label>
                  <input
                    id="waitlist-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className={inputClass}
                    data-testid="waitlist-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="waitlist-phone" className="block text-[13px] font-medium text-[#202124] mb-1.5">
                    Phone number
                  </label>
                  <input
                    id="waitlist-phone"
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className={inputClass}
                    data-testid="waitlist-phone-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "loading" || !isFormValid}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#6C5CE7] hover:bg-[#e8872e] disabled:opacity-60 disabled:cursor-not-allowed text-white text-[14px] font-medium rounded-lg transition-colors shadow-sm"
                  data-testid="waitlist-submit-button"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    "Join the waitlist"
                  )}
                </button>

                {status === "error" && (
                  <p className="text-[13px] text-red-600 text-center" data-testid="waitlist-error">
                    {message}
                  </p>
                )}
              </form>

              <p className="mt-4 text-[11px] text-[#80868b] text-center leading-relaxed">
                We'll only contact you about Helocore updates. No spam, ever.
              </p>
            </>
          ) : (
            <div className="text-center py-4" data-testid="waitlist-success">
              <div className="w-14 h-14 rounded-full bg-[#e6f4ea] flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="w-7 h-7 text-[#34a853]" />
              </div>
              <h2 className="text-[22px] sm:text-[26px] font-normal text-[#202124] leading-tight mb-2">
                {status === "exists" ? "You're already in!" : "You're on the list!"}
              </h2>
              <p className="text-[14px] text-[#5f6368] leading-relaxed max-w-xs mx-auto mb-6">
                {message}
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2.5 text-[14px] font-medium text-[#6C5CE7] border border-[#dadce0] rounded-lg hover:bg-[#f8f9fa] transition-colors"
                data-testid="waitlist-done-button"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WaitlistModal;
